import isEqual from 'react-fast-compare'

import {
  Theme as ColorTheme,
  Palette,
} from '@tribeplatform/design-system/types'
import {
  Network,
  NewTheme as NetworkTheme,
  Themes,
} from '@tribeplatform/gql-client/types'
import type { Appearance } from '@tribeplatform/react-ui-kit/ThemeProvider'
import {
  DEFAULT_STYLE,
  LEGACY_NETWORK_COLOR_SCHEME,
} from '@tribeplatform/react-ui-kit/ThemeProvider'

import { THEME_VERSION_V2 } from '../constants/theme.constants.js'
import { BASE_THEME } from '../themes/base.theme.js'
import {
  convertToTokens,
  ThemeConvertor as LegacyThemeConvertor,
} from '../themes/theme-convertor.js'
import { Theme as CustomizerTheme, ThemeTypography } from '../themes/types.js'
import { resolveColorTheme } from './colors.js'
import { resolveTypography } from './typography.js'

export const isEmptyCustomTheme = (theme: ColorTheme) => {
  return (
    !theme ||
    Object.keys(theme).length === 0 ||
    (Object.keys(theme.light1).length === 0 &&
      Object.keys(theme.light2).length === 0 &&
      Object.keys(theme.light3).length === 0 &&
      Object.keys(theme.dark1).length === 0 &&
      Object.keys(theme.dark2).length === 0)
  )
}

const themeFromTokens = (customizerTheme: CustomizerTheme) => {
  const tokens = customizerTheme.typography
  const { colorTheme: colorThemeName, customTheme } = tokens

  if (!isEmptyCustomTheme(customTheme)) {
    return customTheme
  }

  if (colorThemeName) {
    return resolveColorTheme(colorThemeName)
  }
  return customizerTheme.newColors
}

export const customizerThemeToAppearance = (
  customizerTheme: CustomizerTheme,
): Appearance => {
  const tokens = customizerTheme.typography
  const {
    theme: typographyName,
    style: savedStyle,
    colorThemeSettings,
  } = tokens

  const typography = resolveTypography(typographyName)
  const theme = themeFromTokens(customizerTheme)
  const networkColorScheme =
    colorThemeSettings && Object.keys(colorThemeSettings).length > 0
      ? colorThemeSettings
      : LEGACY_NETWORK_COLOR_SCHEME
  const style =
    savedStyle && Object.keys(savedStyle).length > 0
      ? savedStyle
      : DEFAULT_STYLE

  return {
    theme,
    typography,
    style,
    networkColorScheme,
  }
}

type NetworkOrPartial =
  | Network
  | {
      themes: Themes | null
      activeTheme: NetworkTheme
    }

export const extractCustomizerTheme = (
  network: NetworkOrPartial,
): CustomizerTheme => {
  const legacyConverter = new LegacyThemeConvertor({
    base: BASE_THEME,
    networkOldTheme: network.themes?.active,
    networkTheme: network.activeTheme,
  })

  return legacyConverter.toTheme()
}

export const convertNetworkToAppearance = (
  network: NetworkOrPartial,
): Appearance => {
  // if (network.activeTheme.id === 'v2') {
  // TODO skip converting colors
  // }
  const customizerTheme = extractCustomizerTheme(network)
  return customizerThemeToAppearance(customizerTheme)
}

export const convertAppearanceToNetwork = (
  appearance: Appearance,
  network: Network,
): NetworkTheme => {
  const {
    typography,
    networkColorScheme,
    style,
    theme: customTheme,
  } = appearance

  const presetColorTheme = resolveColorTheme(customTheme.id)
  const isPreset = isEqual(presetColorTheme, {
    ...customTheme,
    id: presetColorTheme.id,
    name: presetColorTheme.name,
  })

  const networkTypography: ThemeTypography = {
    theme: typography.id,
    colorTheme: customTheme.id,
    style,
    colorThemeSettings: networkColorScheme,
    customTheme: isPreset ? undefined : customTheme,
    version: THEME_VERSION_V2,
  }

  const legacyConverter = new LegacyThemeConvertor({
    base: BASE_THEME,
    networkOldTheme: network.themes?.active,
    networkTheme: network.activeTheme,
  })

  const oldNetworkTheme = legacyConverter.toNetworkTheme()

  const networkTheme: NetworkTheme = {
    id: 'v2',
    name: 'v2',
    colors: oldNetworkTheme.colors,
    typography: convertToTokens(
      Object.entries(networkTypography).reduce(
        (preValue, [key, value]) => ({
          ...preValue,
          [key]: JSON.stringify(value),
        }),
        {},
      ),
    ),
  }
  return networkTheme
}

export const generateTheme = async ({ primary, neutral, accent }: Palette) => {
  const response = await fetch(
    `/api/theme/generate?primary=${encodeURIComponent(
      primary,
    )}&neutral=${encodeURIComponent(neutral)}&accent=${encodeURIComponent(
      accent,
    )}`,
  )
  const { data } = await response.json()
  return data as ColorTheme
}
